import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { compose } from 'redux'
import VODPlayer from './VODPlayer'
import withVODEvents from './withVODEvents'
import withDialogs from '../../hocs/withDialogs'
import withPlayerApi from '../../hocs/withPlayerApi'
import { DEFAULT_GROUPTOKEN, GUIDANCE_ACCEPTED } from '../../constants'
import {
  updateEpisodeData,
  fetchNextEpisodeData,
  setPlayerError,
  setApiError,
  setGuidanceAccepted,
  resumeEpisode,
  startEpisode,
  clearStopResumePosition,
} from '../../actions'
import cookies from '../../utils/cookies'
import getError from './getError'

const VODPlayerContainer = ({ playerApi, ...props }) => {
  const dispatch = useDispatch()
  const episodeData = useSelector(state => state.episode.currentEpisode)
  const nextEpisodeData = useSelector(state => state.episode.nextEpisode)
  const stopResume = useSelector(state => state.stopResume)
  const playerApiPath = useSelector(state => state.episode.playerApiPath)
  const playerError = useSelector(state => state.playerError)
  const apiErrorState = useSelector(state => state.apiErrorState)
  useEffect(() => {
    const handleEpisodeChange = (event) => {
      if (event.data && event.data.type === 'EPISODE_CHANGED') {
        dispatch(updateEpisodeData(nextEpisodeData))
        // Fetch the next episode data for the new current episode
        dispatch(fetchNextEpisodeData(nextEpisodeData.guid, playerApi))
      }
    }

    window.addEventListener('message', handleEpisodeChange)

    return () => {
      window.removeEventListener('message', handleEpisodeChange)
    }
  }, [dispatch, nextEpisodeData, playerApi])

  const videoQueryParams = episodeData?.videoQueryParams ? {
    contentType: episodeData.videoQueryParams.contentType,
    matId: episodeData.videoQueryParams.matId,
    guid: episodeData.videoQueryParams.guid,
    programme: episodeData.videoQueryParams.programme,
    video: episodeData.videoQueryParams.video,
    chapter: episodeData.videoQueryParams.chapter,
    title: episodeData.videoQueryParams.title,
    summary: episodeData.videoQueryParams.summary,
    _videoplazaId: episodeData.videoQueryParams._videoplazaId,
    id: episodeData.videoQueryParams.id,
    type: episodeData.videoQueryParams.type,
    groupToken: DEFAULT_GROUPTOKEN,
    isPlayerPlus: episodeData.videoQueryParams.isPlayerPlus,
  } : null

  const stopResumePosition = stopResume[playerApiPath]?.stopResumePosition || 0

  const error = getError({
    episodeResults: episodeData,
    apiErrorState,
    playerError,
  })

  const onErrorStateChange = (err) => {
    if (err?.type === 'player') {
      dispatch(setPlayerError({ error: err }))
    } else if (err?.type === 'api') {
      dispatch(setApiError({ error: err }))
    }
  }

  const handleGuidanceAccepted = () => {
    if (!episodeData?.programme?.guid) return
    dispatch(setGuidanceAccepted({ path: playerApiPath }))
    // Set cookie to show guidance was accepted for this programme
    cookies.set(episodeData.programme.guid, GUIDANCE_ACCEPTED, {
      sameSite: 'strict',
    })
  }

  const handleResumeEpisode = () => {
    dispatch(resumeEpisode({ path: playerApiPath }))
  }

  const handleStartEpisode = () => {
    dispatch(startEpisode({ path: playerApiPath }))
  }

  const handleClearResumePosition = () => {
    dispatch(clearStopResumePosition({
      id: episodeData?.videoQueryParams?.id,
    }))
  }

  return episodeData ? (
    <VODPlayer
      {...props}
      videoQueryParams={videoQueryParams}
      stopResumePosition={stopResumePosition}
      error={error}
      onErrorStateChange={onErrorStateChange}
      onGuidanceAccepted={handleGuidanceAccepted}
      onResumeEpisode={handleResumeEpisode}
      onStartEpisode={handleStartEpisode}
      onClearResumePosition={handleClearResumePosition}
    />
  ) : null
}

VODPlayerContainer.propTypes = {
  playerApi: PropTypes.func.isRequired,
  accessibility: PropTypes.object, // Add accessibility prop type
}

const enhance = compose(
  withVODEvents,
  withDialogs,
  withPlayerApi
)

export default enhance(VODPlayerContainer)
